import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import { SR_BUTTON_THEME_ADOBE_GREY } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';

import './SrPartnerMarketingItemsInstallModal.less';

const SrPartnerMarketingItemsInstallModal = ({
  onClose,
  includesSunlock,
  includesYardSign,
  includesComboLock,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const lockIconMargin = isSmallScreen ? '32px auto 0' : '52px 10px 0';

  return (
    <SrModalV2
      theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
      buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
      onClose={onClose}
      contentPadding="26px 20px 260px"
    >
      <SrBox styleName="SrPartnerMarketingItemsInstallModal">
        <SrBox>
          <SrText size="30" lineHeight="36px" display="block" weight="800">
            What's being installed
          </SrText>
        </SrBox>
        <SrBox marginTop="8px">
          <SrText opacity="0.6">
            Learn more about the marketing items that are being installed at this listing.
          </SrText>
        </SrBox>
        {includesSunlock && (
          <>
            <SrBox margin={lockIconMargin} width="80px" position="relative">
              <img width="80px" height="auto" alt="sunlock" src="/images/sunlock-icon-medium.svg" />
            </SrBox>
            <SrBox marginTop="32px">
              <SrText size="30" lineHeight="36px" display="block" weight="800">
                Sunlock for Self Tours
              </SrText>
            </SrBox>
            <SrBox marginTop="8px">
              <SrText opacity="0.6">
                We'll be replacing the original deadbolt with a Sunlock for self-tours. This means
                your original key will not give you access to the property. &#8202;
              </SrText>
              <SrText weight="800" opacity="0.6">
                After we install the Sunlock, we'll be providing access information!
              </SrText>
            </SrBox>
            <SrBox marginTop="32px">
              <SrText size="21" weight="800">
                Why do we replace the lock?
              </SrText>
            </SrBox>
            <SrBox marginTop="8px">
              <SrText opacity="0.6">
                We replace it to give more renters access using the Sunlock. Sunlocks are secure
                because the renter never gets the physical key. Renters are vetted by an AI-powered
                fraud detection screening service. After passing the screening, the door magically
                unlocks, without giving the renter the physical key.
              </SrText>
            </SrBox>
            <SrBox margin="32px 0 0">
              <SrText
                font="greycliff"
                size="16"
                weight="700"
                color="blueGreycliff"
                lineHeight="25px"
                align="center"
                letterSpacing="0"
                display="block"
              >
                ▶️ Watch a short video about how Sunlocks increase tours!
              </SrText>
            </SrBox>
            <SrBox marginTop="24px" overflow="hidden">
              <iframe
                width="100%"
                height={isSmallScreen ? '190px' : '400px'}
                src="https://www.youtube.com/embed/hzX4YPzRM3I"
                title="YouTube video player"
                frameBorder="0"
                allow={
                  'accelerometer; autoplay; clipboard-write; ' +
                  'encrypted-media; gyroscope; picture-in-picture'
                }
                allowFullScreen
              />
            </SrBox>
          </>
        )}
        {includesComboLock && (
          <>
            <SrBox margin={lockIconMargin} width="80px" position="relative">
              <img
                width="80px"
                height="auto"
                alt="combo lockbox"
                src="/images/combo-lockbox-icon-medium.svg"
              />
            </SrBox>
            <SrBox marginTop="32px">
              <SrText size="30" lineHeight="36px" display="block" weight="800">
                Combo Lockbox
              </SrText>
            </SrBox>
            {includesSunlock ? (
              <>
                <SrBox marginTop="8px">
                  <SrText opacity="0.6">
                    The combo lockbox will have two physical keys inside.
                  </SrText>
                </SrBox>
                <SrBox marginTop="8px">
                  <SrText weight="800" opacity="0.6">
                    One is the owner's key to the original deadbolt. &#8202;
                  </SrText>
                  <SrText opacity="0.6">
                    Remember the owner's key doesn't work on the installed Sunlock.
                  </SrText>
                </SrBox>
                <SrBox marginTop="8px">
                  <SrText weight="800" opacity="0.6">
                    The other key unlocks the installed Sunlock deadbolt. &#8202;
                  </SrText>
                  <SrText opacity="0.6">
                    You can use the physical key to open the Sunlock deadbolt if it happens to run
                    out of battery. &#8202;
                  </SrText>
                  <SrText weight="800" opacity="0.6">
                    Renters will never have access to these keys.
                  </SrText>
                </SrBox>
              </>
            ) : (
              <>
                <SrBox marginTop="8px">
                  <SrText opacity="0.6">
                    The combo lockbox will have the owner's key to the original deadbolt. &#8202;
                  </SrText>
                  <SrText weight="800" opacity="0.6">
                    Renters will never have access to this key.
                  </SrText>
                </SrBox>
              </>
            )}
          </>
        )}
        <SrBox
          margin={isSmallScreen ? '56px auto 0' : '72px 10px 0'}
          width="80px"
          position="relative"
        >
          <img width="80px" height="auto" alt="supra lockbox" src="/images/supra-icon-medium.svg" />
        </SrBox>
        <SrBox marginTop="32px">
          <SrText size="30" lineHeight="36px" display="block" weight="800">
            Supra Lockbox
          </SrText>
        </SrBox>
        <SrBox marginTop="8px">
          <SrText opacity="0.6">
            {'The Supra will be used by licensed real estate agents to access the home for property tours. ' +
              'Inside the Supra is one physical key to open the ' +
              `${includesSunlock ? 'Sunlock deadbolt' : 'door'}. ` +
              'Supra lockboxes are commonly used in the real estate industry as a secure way for licensed ' +
              'agents to tour homes and for Sunroom to track those tours.'}
          </SrText>
        </SrBox>
        {includesYardSign && (
          <>
            <SrBox
              margin={isSmallScreen ? '56px auto 0' : '72px 10px 0'}
              width="80px"
              position="relative"
            >
              <img
                width="80px"
                height="auto"
                alt="yard sign"
                src="/images/yard-sign-icon-medium.svg"
              />
            </SrBox>
            <SrBox marginTop="32px">
              <SrText size="30" lineHeight="36px" display="block" weight="800">
                Yard Sign
              </SrText>
            </SrBox>
            <SrBox marginTop="8px">
              <SrText opacity="0.6">
                We’ll set up a yard sign at your property to engage drive by traffic. The yard sign
                will have a custom URL on it so the renter can quickly find your listing on their
                phone and check out the details about the listing, including price and photos.
              </SrText>
            </SrBox>
          </>
        )}
      </SrBox>
    </SrModalV2>
  );
};

SrPartnerMarketingItemsInstallModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  includesSunlock: PropTypes.bool.isRequired,
  includesYardSign: PropTypes.bool.isRequired,
  includesComboLock: PropTypes.bool.isRequired,
};

SrPartnerMarketingItemsInstallModal.displayName = 'SrPartnerMarketingItemsInstallModal';

export default SrPartnerMarketingItemsInstallModal;
